import { Card, CardContent } from '@/components/ui/card'
import { Account } from '@/domain/accounts/account'
import { Agreement, UpdateAgreementData } from '@/domain/agreements/agreement'
import { DeathData } from '@/domain/deathData/deathData'
import { Offering } from '@/domain/offerings/offering'
import { useCreateDeathData, useDeathDataByContract, useDeleteDeathData, useUpdateDeathData } from '@/hooks/api/useDeathData'
import { toast } from '@/hooks/use-toast'
import { useState } from 'react'
import { ManualEditDeathDataModal } from '../../parcels/listing/ManualEditDeathDataModal'
import { AccountStatusCard } from './main-components/AccountStatusCard'
import { AgreementDetailsHeader } from './main-components/AgreementDetailsHeader'
import { AgreementPaymentsInfo } from './AgreementPaymentsInfo'
import { DeathDataList } from './main-components/DeathDataList'
import { DocumentsCard } from './documents/DocumentsCard'

export interface CremationsCmpProps {
  agreement: Agreement
  offering?: Offering
  contractAccount?: Account
  userAccounts?: Account[]
  totalBalance: number
  isLoadingAccounts: boolean
  onViewAccountMovements: () => void
  isEditing?: boolean
  isSaving?: boolean
  onSave?: (changes: UpdateAgreementData) => void
  onCancel?: () => void
  onEdit?: () => void
}

export const CremationsCmp = (props: CremationsCmpProps) => {
  const {
    agreement, offering, contractAccount, userAccounts, totalBalance, isLoadingAccounts,
    onViewAccountMovements, isEditing, isSaving, onSave, onCancel, onEdit
  } = props

  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editingRecord, setEditingRecord] = useState<DeathData | null>(null)
  const { data: deathData } = useDeathDataByContract(agreement.id)

  const updateDeathDataMutation = useUpdateDeathData()
  const createDeathDataMutation = useCreateDeathData()
  const deleteDeathDataMutation = useDeleteDeathData()

  const handleSave = async (record: DeathData) => {
    if (record.id) {
      await updateDeathDataMutation.mutateAsync({
        id: record.id,
        data: record
      })
    } else {
      await createDeathDataMutation.mutateAsync({
        ...record,
        contractId: agreement.id
      })
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 grid-cols-1 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <AgreementDetailsHeader
            agreement={agreement}
            offering={offering}
            totalBalance={totalBalance}
            actions={{
              isEditing,
              isSaving,
              onSave,
              onCancel,
              onEdit
            }}
            data={{
              userAccounts,
              contractAccount,
              isLoadingAccounts,
            }}
          />
          <CardContent className="space-y-6">
            <DeathDataList
              deathData={deathData || []}
              serviceType={offering?.serviceType}
              onEdit={(record) => {
                setEditingRecord(record)
                setIsEditModalOpen(true)
              }}
              onDelete={async (record) => {
                if (deathData?.length === 1) {
                  toast({
                    title: 'Error',
                    description: 'No se puede eliminar el último registro',
                    variant: 'destructive',
                  })
                  return
                }
                if (record.id && confirm('¿Está seguro de que desea eliminar este fallecido?')) {
                  await deleteDeathDataMutation.mutateAsync(record.id)
                }
              }}
              onAdd={() => {
                setEditingRecord(null)
                setIsEditModalOpen(true)
              }}
            />
          </CardContent>
        </Card>

        <div className="space-y-4">
          {contractAccount && (
            <AccountStatusCard
              contractAccount={contractAccount}
              onViewMovements={onViewAccountMovements}
            />
          )}
          <DocumentsCard agreementId={agreement.id} />
        </div>
      </div>

      <AgreementPaymentsInfo
        agreement={agreement}
        serviceType={offering?.serviceType}
      />

      {isEditModalOpen && (
        <ManualEditDeathDataModal
          isOpen={isEditModalOpen}
          onClose={() => { setIsEditModalOpen(false); setEditingRecord(null); }}
          record={editingRecord}
          onSave={handleSave}
          serviceType={offering?.serviceType}
        />
      )}
    </div>
  )
}
